import { setInstallmentAmountEditable } from '../manager/pledges/pledge-edit-ongoing-modal';
import {
    setRecurringScheduleData,
    setPledgeTotals,
    setInstallmentAmountNote,
    updateOngoingDurationOption,
    updateRecurringInstallmentAmount,
} from '../pledge-pay/pledge-pay-recurring.js';
import { formatCurrency } from '../pledge-pay/pledge-pay-utils.js';
import { money } from './recurring-edit-plan.js';
import {
    loadEditRecurringSavedCards,
    setEditRecurringLayout,
    setManualCardCollapsed,
    captureEditRecurringBaseline,
    updateEditRecurringModePanel,
    syncEditRecurringDisabledHints,
    syncLinkedPledgesList,
} from './ongoing-pledge-edit-ui.js';

function otherScheduleRemainingFromData(data) {
    const thisBalance = money(data.balance_due);
    const scheduleBalance = Number(data.schedule_balance_due);
    if (Number.isFinite(scheduleBalance)) {
        return Math.max(0, money(scheduleBalance) - thisBalance);
    }
    const pledges = data.recurring_schedule && data.recurring_schedule.pledges;
    if (!Array.isArray(pledges)) {
        return 0;
    }
    const thisId = Number(data.id);
    return pledges.reduce((sum, pledge) => {
        if (Number(pledge.id) === thisId) {
            return sum;
        }
        return sum + money(pledge.balance_due);
    }, 0);
}

function setupOngoingDurationFields($modal, data) {
    if (data.ongoing_indefinitely) {
        $modal.find('#on_num_left').val(0).change();
        $modal.find('#on_orig_duration').val('Ongoing');
    } else {
        $modal.find('#on_num_left').val(data.num_left).change();
        $modal.find('#on_orig_duration').val(data.original_duration + 'x');
    }
    updateOngoingDurationOption($modal);
}

function setOngoingCampaignSelect($modal, data) {
    const $campaign = $modal.find('#on_campaign_id');
    if (!$campaign.length) {
        return;
    }
    $campaign.find('option[data-unlisted-campaign]').remove();
    $campaign.prop('disabled', false);

    const campaignId = data.campaign_id;
    if (campaignId == null || campaignId === '') {
        $campaign.val('');
        return;
    }

    const id = String(campaignId);
    if (!$campaign.find('option[value="' + id + '"]').length) {
        $campaign.append(
            '<option value="' + id + '" data-unlisted-campaign="1" data-gateway-id="'
            + (Number(data.campaign_gateway_id) || 0) + '">' + (data.title || 'Current campaign') + '</option>'
        );
        if (Number(data.campaign_archived) !== 1) {
            $campaign.prop('disabled', true);
        }
    }
    $campaign.val(id);
}

function populateOngoingEditModal($modal, data, pledgeType, options) {
    const opts = options || {};
    const ongoingId = opts.ongoingId != null ? opts.ongoingId : data.id;

    $modal.find('#ongoing_id').val(ongoingId);
    $modal.find('#ongoing_type').val(pledgeType);

    if (opts.setUserFields !== false) {
        $modal.find('#ongoing_user_id').val(data.user_id ?? '');
        if ($modal.find('#ongoing_user_name').length) {
            $modal.find('#ongoing_user_name').val(data.name ?? '');
            $modal.find('#ongoing_user_email').val(data.email ?? '');
            $modal.find('#ongoing_user_phone').val(data.phone ?? '');
        }
    }

    if (opts.modalTitle) {
        $modal.find('#modal_title').text(opts.modalTitle);
    }

    if (pledgeType === 'Donation') {
        const scheduleData = null;
        setRecurringScheduleData(scheduleData);
        if (opts.onRecurringScheduleData) {
            opts.onRecurringScheduleData(scheduleData);
        }
        $modal.data('ongoingType', 'Donation');
        $modal.find('#pledge_totals_row').addClass('d-none');
        $modal.find('#installment_amount_note').addClass('d-none').text('');
        const donationInstallmentAmount = data.installment_amount ?? data.amount ?? 0;
        $modal.find('#on_amount').val(formatCurrency(donationInstallmentAmount));
        setInstallmentAmountEditable($modal, true);
        $modal.find('#on_campaign_id').prop('required', true);
    } else {
        setRecurringScheduleData(data);
        if (opts.onRecurringScheduleData) {
            opts.onRecurringScheduleData(data);
        }
        $modal.data('ongoingType', 'Pledge');
        setInstallmentAmountEditable($modal, true);
        setPledgeTotals($modal);
        setInstallmentAmountNote($modal);
        $modal.find('#pledge_amount_error').prop('hidden', true).text('');
        $modal.find('#pledge_amount_input').css('border-color', '');
    }

    $modal.find('#on_frequency_id').val(data.frequency_id).change();

    const $desc = $modal.find('#on_custom_desc');
    if ($desc.length) {
        $desc.val(data.comments ?? data.description ?? '');
    }

    $modal.data('editRecurringApplyingMode', true);
    setupOngoingDurationFields($modal, data);

    $modal.find('#on_amount').val(formatCurrency(data.installment_amount));

    updateRecurringInstallmentAmount($modal, pledgeType);
    $modal.removeData('editRecurringApplyingMode');

    const $onAmount = $modal.find('#on_amount');
    $onAmount.data('initial', $onAmount.val());

    const errSelector = opts.amountErrorSelector || '#enter_amt_err';
    $modal.find(errSelector).hide();

    setOngoingCampaignSelect($modal, data);
    setEditRecurringLayout($modal, pledgeType, data);
    setManualCardCollapsed($modal, true);
    $modal.data('editRecurringPledgeData', data);
    $modal.data('editRecurringOtherScheduleRemaining', otherScheduleRemainingFromData(data));
    syncLinkedPledgesList($modal, data);
    syncEditRecurringDisabledHints($modal);
    captureEditRecurringBaseline($modal);
    const minTransAmt = Number($modal.data('editOngoingMinTransAmt')) || 0;
    updateEditRecurringModePanel($modal, minTransAmt);

    const config = $modal.data('editOngoingConfig') || {};
    loadEditRecurringSavedCards(
        $modal,
        config.savedPaymentsUrl,
        $modal.find('#ongoing_user_id').val(),
        data,
        pledgeType
    );
}

export {
    populateOngoingEditModal,
    setupOngoingDurationFields,
};
